import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { classificationService } from '../../services/classificationService';
import { filterProductClassificationHierarchyExcludingRecetaMirror } from '../../utils/productClassificationFilters';

const Hierarchy = () => {
  const [hierarchy, setHierarchy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const hasLoaded = useRef(false);

  useEffect(() => {
    // Evitar doble carga en React StrictMode
    if (!hasLoaded.current) {
      hasLoaded.current = true;
      loadHierarchy();
    }
  }, []);

  const loadHierarchy = async () => {
    try {
      setLoading(true);
      console.log('📁 [Hierarchy] Cargando jerarquía...');
      
      const response = await classificationService.getHierarchy();
      
      console.log('✅ [Hierarchy] Jerarquía cargada exitosamente');
      console.log('📊 [Hierarchy] Respuesta:', response);
      
      if (response.success) {
        const data = filterProductClassificationHierarchyExcludingRecetaMirror(response.data);
        setHierarchy(data);
        // Expandir todas las categorías por defecto
        const categoryIds = new Set(data.map((cat) => cat.id));
        setExpandedCategories(categoryIds);
        console.log('💾 [Hierarchy] Jerarquía guardada en estado');
      } else {
        console.warn('⚠️ [Hierarchy] Respuesta sin éxito:', response);
      }
    } catch (error) {
      console.error('❌ [Hierarchy] Error al cargar jerarquía:', error);
      console.error('📊 [Hierarchy] Respuesta del servidor:', error.response?.data);
    } finally {
      setLoading(false);
      console.log('🏁 [Hierarchy] Proceso de carga finalizado');
    }
  };

  const toggleCategory = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  // Calcular estadísticas
  const totalCategories = hierarchy.length;
  const totalSubcategories = hierarchy.reduce((sum, cat) => sum + (cat.subcategories?.length || 0), 0);
  const totalFamilies = hierarchy.reduce((sum, cat) => 
    sum + (cat.subcategories?.reduce((subSum, sub) => subSum + (sub.families?.length || 0), 0) || 0), 0
  );
  const totalActiveCategories = hierarchy.filter(cat => cat.is_active).length;
  const totalActiveSubcategories = hierarchy.reduce((sum, cat) => 
    sum + (cat.subcategories?.filter(sub => sub.is_active).length || 0), 0
  );
  const totalActiveFamilies = hierarchy.reduce((sum, cat) => 
    sum + (cat.subcategories?.reduce((subSum, sub) => 
      subSum + (sub.families?.filter(fam => fam.is_active).length || 0), 0) || 0), 0
  );

  if (loading) {
    return (
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center items-center h-64">
            <div className="text-lg text-gray-600">Cargando jerarquía...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-800">Vista Jerárquica</h1>
                <p className="text-gray-600 mt-1">Clasificación completa de productos (3 niveles)</p>
              </div>
              <div className="flex gap-2">
                <Link
                  to="/classification/categories"
                  className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors"
                >
                  Gestionar Categorías
                </Link>
              </div>
            </div>

            {/* Breadcrumb */}
            <nav className="text-sm text-gray-600 mb-4">
              <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
              <span className="mx-2">/</span>
              <Link to="/classification" className="hover:text-teal-600">Clasificación</Link>
              <span className="mx-2">/</span>
              <span className="text-gray-800">Vista Jerárquica</span>
            </nav>
          </div>

          {/* Stats - Encima de la tabla */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="text-sm text-gray-600">Total Categorías</div>
              <div className="text-2xl font-bold text-teal-600">{totalCategories}</div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="text-sm text-gray-600">Categorías Activas</div>
              <div className="text-2xl font-bold text-green-600">{totalActiveCategories}</div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="text-sm text-gray-600">Total Subcategorías</div>
              <div className="text-2xl font-bold text-purple-600">{totalSubcategories}</div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="text-sm text-gray-600">Subcategorías Activas</div>
              <div className="text-2xl font-bold text-green-600">{totalActiveSubcategories}</div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="text-sm text-gray-600">Total Familias</div>
              <div className="text-2xl font-bold text-gray-700">{totalFamilies}</div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="text-sm text-gray-600">Familias Activas</div>
              <div className="text-2xl font-bold text-green-600">{totalActiveFamilies}</div>
            </div>
          </div>

          {/* Hierarchy Tree */}
          <div className="bg-white rounded-lg shadow-md p-6">
            {hierarchy.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No hay datos de clasificación disponibles
              </div>
            ) : (
              <div className="space-y-4">
                {hierarchy.map((category) => (
                  <div key={category.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    {/* Category Level */}
                    <div
                      className="bg-teal-50 px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-teal-100 transition-colors"
                      onClick={() => toggleCategory(category.id)}
                    >
                      <div className="flex items-center gap-3">
                        <svg
                          className={`w-5 h-5 text-teal-600 transition-transform ${
                            expandedCategories.has(category.id) ? 'rotate-90' : ''
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <div>
                          <span className="font-semibold text-teal-800">{category.code}</span>
                          <span className="ml-2 text-gray-800 font-medium">{category.name}</span>
                        </div>
                        <span className="text-xs text-gray-500">(Nivel 1)</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-gray-600">
                          {category.subcategories?.length || 0} subcategoría(s)
                        </span>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          category.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {category.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                    </div>

                    {/* Subcategories Level */}
                    {expandedCategories.has(category.id) && category.subcategories && category.subcategories.length > 0 && (
                      <div className="bg-white">
                        {category.subcategories.map((subcategory) => (
                          <div key={subcategory.id} className="border-t border-gray-200">
                            <div className="bg-purple-50 px-8 py-3 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-4 h-4 border-l-2 border-t-2 border-purple-300"></div>
                                <div>
                                  <span className="font-semibold text-purple-800">{subcategory.code}</span>
                                  <span className="ml-2 text-gray-800">{subcategory.name}</span>
                                </div>
                                <span className="text-xs text-gray-500">(Nivel 2)</span>
                              </div>
                              <div className="flex items-center gap-4">
                                <span className="text-sm text-gray-600">
                                  {subcategory.families?.length || 0} familia(s)
                                </span>
                                <span className={`px-2 py-1 text-xs rounded-full ${
                                  subcategory.is_active 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {subcategory.is_active ? 'Activo' : 'Inactivo'}
                                </span>
                              </div>
                            </div>

                            {/* Families Level */}
                            {subcategory.families && subcategory.families.length > 0 && (
                              <div className="bg-gray-50">
                                {subcategory.families.map((family) => (
                                  <div
                                    key={family.id}
                                    className="px-12 py-2 flex items-center justify-between border-t border-gray-200 hover:bg-gray-100"
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className="w-4 h-4 border-l-2 border-t-2 border-gray-300"></div>
                                      <div>
                                        <span className="font-semibold text-gray-700">{family.code}</span>
                                        <span className="ml-2 text-gray-700">{family.name}</span>
                                      </div>
                                      <span className="text-xs text-gray-500">(Nivel 3)</span>
                                    </div>
                                    <span className={`px-2 py-1 text-xs rounded-full ${
                                      family.is_active 
                                        ? 'bg-green-100 text-green-800' 
                                        : 'bg-red-100 text-red-800'
                                    }`}>
                                      {family.is_active ? 'Activo' : 'Inactivo'}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hierarchy;
